/**
 * Luxe DesignScene — right-column sub-components (Phase 1A-S closeout).
 * Extracted out of SchematicBuilder.jsx to keep individual files
 * traversable by the visual-edits babel plugin.
 */
import { useMemo, useState, useEffect, useRef } from "react";
import {
  Trash2, Copy, Lock, LockOpen, Layers, Search, AlertTriangle,
  AlignCenter, AlignHorizontalJustifyCenter,
  ArrowUpToLine, ArrowDownToLine, ArrowLeftToLine, ArrowRightToLine,
  ChevronLeft, ChevronRight, Minus, Plus,
  ChevronsUp, ChevronsDown, ChevronUp, ChevronDown,
} from "lucide-react";
import MaterialPicker from "@/components/MaterialPicker";
import api from "@/lib/api";
import { LightingPropertiesPanel, LIGHTING_TYPES,
         MaterialMappingPanel } from "@/components/PhaseTwoAiiPanels";
import { getComponent, slotsFor } from "@/lib/componentRegistry";
import { HostTreeList } from "@/components/SchematicHostBranch";
import { CabinetBayPreview, FireplaceFacePreview } from "@/components/CabinetBayPreview";

// ── Small helpers ────────────────────────────────────────────────────
export function wxhFromDiagonal(diag) {
  const d_mm = diag * 25.4;
  const w = Math.round(d_mm * 16 / Math.sqrt(16 * 16 + 9 * 9));
  const h = Math.round(d_mm * 9  / Math.sqrt(16 * 16 + 9 * 9));
  return { w, h };
}
export function deriveDiagonalIn(w, h) {
  return Math.round(Math.sqrt((w || 0) ** 2 + (h || 0) ** 2) / 25.4);
}
export const LED_MODE_SIDES = {
  perimeter_all: ["top", "bottom", "left", "right"],
  top: ["top"], bottom: ["bottom"], left: ["left"], right: ["right"],
  top_sides: ["top", "left", "right"],
  bottom_sides: ["bottom", "left", "right"],
};
export function ledModeFor(item) {
  const att = item.attachment;
  if (!att || att.mode !== "perimeter") return "linear";
  const sides = (att.sides || []).slice().sort().join("|");
  for (const [k, v] of Object.entries(LED_MODE_SIDES)) {
    if (v.slice().sort().join("|") === sides) return k;
  }
  return "perimeter_all";
}
export function attachmentForMode(mode) {
  if (mode === "linear") return null;
  return { mode: "perimeter", sides: LED_MODE_SIDES[mode] || ["top", "bottom", "left", "right"] };
}
export function displayName(it) {
  const def = getComponent(it.type) || {};
  return it.label || def.label || it.type;
}
export function subtitleFor(it) {
  if (it.type === "tv") {
    const d = it.diagonal_inches || deriveDiagonalIn(it.width, it.height);
    return `${d}"`;
  }
  if (it.type === "led_strip") {
    return it.attachment?.mode === "perimeter" ? "Perimeter" : "Linear";
  }
  return null;
}

const LED_PERIMETER_MODES = [
  { value: "linear",        label: "Linear (manual length)" },
  { value: "perimeter_all", label: "Full Perimeter" },
  { value: "top",           label: "Top Only" },
  { value: "bottom",        label: "Bottom Only" },
  { value: "left",          label: "Left Only" },
  { value: "right",         label: "Right Only" },
  { value: "top_sides",     label: "Top + Sides" },
  { value: "bottom_sides",  label: "Bottom + Sides" },
];
const SUBSTRATE_OPTIONS = [
  { value: "plasterboard_new",      label: "Plasterboard — New" },
  { value: "plasterboard_existing", label: "Plasterboard — Existing" },
  { value: "fibre_cement",          label: "Fibre Cement / FC" },
  { value: "masonry",               label: "Masonry / Existing Wall" },
  { value: "plywood",               label: "Plywood / Backing" },
  { value: "none",                  label: "No Additional Substrate" },
  { value: "custom",                label: "Custom" },
];
const SUBSTRATE_APPLICABLE = new Set([
  "stone_panel", "timber_panel", "acoustic_panel", "cladding", "feature_panel",
]);

// Registry-driven capability check. Single source of truth — the
// component-registry's `category === "Cabinetry"` is authoritative.
// Do not duplicate cabinetry type lists elsewhere.
function isCabinetComponent(type) {
  const def = getComponent(type);
  return !!def && def.category === "Cabinetry";
}

// ── Cabinet + Fireplace canonical configuration vocabularies ─────────
const CABINET_LAYOUT_MODES = [
  { v: "even_split",   l: "Even Split" },
  { v: "custom_split", l: "Custom Split" },
  { v: "mixed",        l: "Mixed" },
];
const CABINET_BAY_KINDS = [
  { v: "door",   l: "Door" },
  { v: "drawer", l: "Drawer Stack" },
  { v: "open",   l: "Open" },
];
const DOOR_HINGE_OPTIONS = [
  { v: "left",  l: "Left hinge" },
  { v: "right", l: "Right hinge" },
  { v: "pair",  l: "Pair (double)" },
  { v: "none",  l: "None / n/a" },
];
const CABINET_SNAP_OPTIONS = [
  { v: 0,  l: "Exact / Off" },
  { v: 1,  l: "1 mm" },
  { v: 5,  l: "5 mm" },
  { v: 10, l: "10 mm" },
  { v: 25, l: "25 mm" },
  { v: 50, l: "50 mm" },
];

// Registry-driven check: a component supports clearance if the
// registry entry declares `hasClearances: true`. Plus a small set of
// well-known equipment types kept as an explicit legacy fallback so
// legacy registry entries without the capability flag still work.
const _LEGACY_CLEARANCE_TYPES = new Set([
  "tv", "soundbar", "speaker", "fireplace", "fireplace_recess",
]);
function itemHasClearances(item) {
  const def = getComponent(item.type);
  return !!(def && (def.hasClearances || _LEGACY_CLEARANCE_TYPES.has(item.type)));
}

const FIREPLACE_SIDED_TYPES = [
  { v: "1_sided_front",           l: "1-sided · Front",             faces: ["front"] },
  { v: "2_sided_front_left",      l: "2-sided · Front + Left",      faces: ["front", "left"] },
  { v: "2_sided_front_right",     l: "2-sided · Front + Right",     faces: ["front", "right"] },
  { v: "2_sided_see_through",     l: "2-sided · See-through (F+R)", faces: ["front", "rear"] },
  { v: "3_sided_front_left_right",l: "3-sided · F + L + R",         faces: ["front", "left", "right"] },
  { v: "4_sided",                 l: "4-sided · F + L + R + B",     faces: ["front", "left", "right", "rear"] },
];


// ── Sticky action header ─────────────────────────────────────────────
export function SelectedHeader({ item, onLock, onDuplicate, onDelete }) {
  const def = getComponent(item.type) || {};
  const sub = subtitleFor(item);
  return (
    <div className="bg-card border-b border-border px-3 py-2 flex items-center gap-2 flex-wrap"
         data-testid="selected-header">
      <div className="min-w-0 flex-1 basis-full sm:basis-auto">
        <div className="text-[13px] font-medium truncate" data-testid="selected-name">
          {displayName(item)}
          {sub ? <span className="text-muted-foreground text-[11px] font-normal"> — {sub}</span> : null}
        </div>
        <div className="text-[10px] text-muted-foreground truncate">
          {def.label || item.type}{item.locked ? " · Locked" : ""}
        </div>
      </div>
      <button type="button" data-testid="prop-lock" data-alias="btn-lock-toggle"
              onClick={onLock}
              aria-label={item.locked ? "Unlock" : "Lock"}
              className="w-9 h-9 inline-flex items-center justify-center border border-border hover:border-foreground transition-colors shrink-0"
              title={item.locked ? "Unlock" : "Lock"}>
        {item.locked
          ? <Lock className="w-5 h-5" strokeWidth={1.75}/>
          : <LockOpen className="w-5 h-5" strokeWidth={1.75}/>}
        <span className="sr-only">{item.locked ? "Unlock" : "Lock"}</span>
      </button>
      <button type="button" data-testid="prop-duplicate" data-alias="btn-duplicate-schematic"
              onClick={onDuplicate}
              disabled={item.locked}
              aria-label="Duplicate"
              className="w-9 h-9 inline-flex items-center justify-center border border-border hover:border-foreground transition-colors disabled:opacity-40 shrink-0"
              title="Duplicate">
        <Copy className="w-5 h-5" strokeWidth={1.75}/>
        <span className="sr-only">Duplicate</span>
      </button>
      <button type="button" data-testid="btn-delete" data-alias="btn-delete-schematic"
              onClick={onDelete}
              disabled={item.locked}
              aria-label={item.locked ? "Unlock to delete" : "Delete"}
              title={item.locked ? "Unlock to delete" : "Delete"}
              className="w-9 h-9 inline-flex items-center justify-center border border-[color:hsl(var(--destructive))] text-[color:hsl(var(--destructive))] hover:bg-[color:hsl(var(--destructive))] hover:text-white transition-colors disabled:opacity-40 shrink-0">
        <Trash2 className="w-5 h-5" strokeWidth={1.75}/>
        <span className="sr-only">Delete</span>
      </button>
    </div>
  );
}


// ── NumField / SaveIndicator / EstimateIndicator ─────────────────────
/**
 * App-wide draft-commit numeric input.
 *
 * The UI displays a temporary string DRAFT while the user types; the
 * canonical numeric value is only updated on blur / Enter. This is the
 * pattern approved for Bay Count, custom drawer heights and shelf
 * positions — extended here so every numeric field in the app behaves
 * consistently.
 *
 * Behaviour highlights:
 *  - Focus selects the current value so an existing `0` is instantly
 *    replaced by the first typed digit (no "03500" leading-zero bug).
 *  - Empty draft is allowed while typing. An empty commit restores the
 *    previous canonical value (never silently writes NaN).
 *  - Decimal entry works when `step` allows it (default step is 1 →
 *    integer commit; pass step=0.5 or step="any" for decimals).
 *  - Field-specific `min` / `max` are enforced on commit only. Never
 *    clamped during a keystroke.
 *  - Escape reverts the draft to the last committed value.
 *
 * NumField remains a compatible drop-in replacement — callers passing
 * `value` + `onChange` (and optional `min`, `max`, `step`) require no
 * further changes.
 */
export function NumField({ testid, label, value, onChange,
                          min, max, step = 1, className = "" }) {
  const canonical = value ?? 0;
  const allowsDecimal = step !== 1 && step !== "1";
  const [draft, setDraft] = useState(String(canonical));
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    // Sync down from canonical when the incoming value changes AND
    // the input is not currently focused. Prevents draft clobber
    // while the user is mid-edit but keeps the field in sync with
    // undo/redo, external mutations and remote autosave.
    if (!focused) setDraft(String(canonical));
  }, [canonical, focused]);
  const commit = () => {
    if (draft === "" || draft === "-" || draft === "." || draft === "-.") {
      setDraft(String(canonical));
      return;
    }
    const n = allowsDecimal ? parseFloat(draft) : parseInt(draft, 10);
    if (Number.isNaN(n)) {
      setDraft(String(canonical));
      return;
    }
    let clamped = n;
    if (typeof min === "number" && clamped < min) clamped = min;
    if (typeof max === "number" && clamped > max) clamped = max;
    onChange(clamped);
    setDraft(String(clamped));
  };
  const onKeyDown = (e) => {
    if (e.key === "Enter") { e.preventDefault(); commit(); e.currentTarget.blur(); }
    else if (e.key === "Escape") { setDraft(String(canonical)); e.currentTarget.blur(); }
  };
  const filter = (raw) => {
    // Strip anything other than digits, optional single minus at start,
    // optional single dot (only when decimals are allowed).
    let out = String(raw).replace(/[^0-9.\-]/g, "");
    if (out.indexOf("-") > 0) out = out.replace(/-/g, "");
    if (!allowsDecimal) out = out.replace(/\./g, "");
    // Collapse multiple decimals to a single one.
    const parts = out.split(".");
    if (parts.length > 2) out = parts[0] + "." + parts.slice(1).join("");
    return out;
  };
  const onFocus = (e) => {
    setFocused(true);
    // Select the whole value on first focus so tapping a `0` and
    // typing `3500` yields exactly `3500`, not `03500`. Users can
    // still place the caret manually after the selection collapses.
    try { e.currentTarget.select(); } catch (_) { /* ignore */ }
  };
  return (
    <div className="min-w-0">
      {label && (
        <label className="overline text-[10px] block mb-1 whitespace-nowrap">{label}</label>
      )}
      <input data-testid={testid}
             type="text"
             inputMode={allowsDecimal ? "decimal" : "numeric"}
             pattern={allowsDecimal ? "[0-9.\\-]*" : "[0-9\\-]*"}
             className={`luxe-input w-full min-w-0 ${className}`}
             value={draft}
             onFocus={onFocus}
             onChange={(e) => setDraft(filter(e.target.value))}
             onBlur={() => { setFocused(false); commit(); }}
             onKeyDown={onKeyDown}/>
    </div>
  );
}
export function SaveIndicator({ state }) {
  const map = {
    idle: ["", ""],
    saving: ["Saving…", "text-muted-foreground"],
    saved: ["Saved", "text-emerald-600"],
    offline: ["Offline · saved locally", "text-amber-600"],
    error: ["Save failed", "text-red-600"],
  };
  const [txt, cls] = map[state] || ["", ""];
  return <span className={`text-[10px] uppercase tracking-widest ${cls}`} data-testid="save-status">{txt}</span>;
}
export function EstimateIndicator({ status }) {
  if (status.current === null) return null;
  const dirty = status.dirty;
  return (
    <span data-testid="estimate-status"
          className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border ${dirty ? "border-amber-500 text-amber-700" : "border-emerald-500 text-emerald-700"}`}>
      {dirty ? "Estimate needs sync" : "Estimate current"}
    </span>
  );
}


// ── Clearances editor ────────────────────────────────────────────────
const CLEARANCE_REF_TYPES = [
  { value: "ffl",            label: "FFL / Floor" },
  { value: "ceiling",        label: "Ceiling" },
  { value: "left_wall",      label: "Left wall edge" },
  { value: "right_wall",     label: "Right wall edge" },
  { value: "wall_centre",    label: "Wall centreline" },
  { value: "host_edge",      label: "Host edge" },
  { value: "component_edge", label: "Another component" },
];
const CLEARANCE_MODES = [
  { value: "preferred", label: "Preferred" },
  { value: "minimum",   label: "Minimum" },
  { value: "exact",     label: "Exact" },
];
export function ClearancesEditor({ item, siblings, onChange }) {
  const list = item.clearances || [];
  const update = (i, patch) => onChange(list.map((c, idx) => idx === i ? { ...c, ...patch } : c));
  const add = () => onChange([...list, { ref_type: "ffl", mode: "preferred", value_mm: 0, enabled: true }]);
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  return (
    <div className="pt-3 border-t border-border" data-testid="clearances-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="overline text-[10px]">Clearances</div>
        <button data-testid="btn-add-clearance" onClick={add}
                className="text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground">
          + Add clearance
        </button>
      </div>
      {list.length === 0 ? (
        <div className="text-[10px] text-muted-foreground" data-testid="clearances-empty">
          No clearances set. Add one to constrain distance to floor, ceiling, walls or another component.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="clearances-list">
          {list.map((c, i) => (
            <ClearanceRow key={i} idx={i} clearance={c} siblings={siblings}
                          onUpdate={update} onRemove={remove}/>
          ))}
        </ul>
      )}
    </div>
  );
}
function ClearanceRow({ idx, clearance, siblings, onUpdate, onRemove }) {
  const c = clearance;
  return (
    <li className="grid grid-cols-[1fr_auto_auto_auto] gap-1 items-center" data-testid={`clearance-row-${idx}`}>
      <select data-testid={`clearance-ref-${idx}`} className="luxe-input text-[11px]"
              value={c.ref_type || "ffl"}
              onChange={(e) => onUpdate(idx, { ref_type: e.target.value })}>
        {CLEARANCE_REF_TYPES.map((r) => (
          <option key={r.value} value={r.value}>{r.label}</option>
        ))}
      </select>
      <select data-testid={`clearance-mode-${idx}`} className="luxe-input text-[11px] w-20"
              value={c.mode || "preferred"}
              onChange={(e) => onUpdate(idx, { mode: e.target.value })}>
        {CLEARANCE_MODES.map((m) => (
          <option key={m.value} value={m.value}>{m.label}</option>
        ))}
      </select>
      <DraftNumInput testid={`clearance-value-${idx}`}
                     value={c.value_mm ?? 0}
                     min={0}
                     className="luxe-input text-[11px] w-20 min-w-0"
                     placeholder="mm"
                     onCommit={(v) => onUpdate(idx, { value_mm: v })}/>
      <button data-testid={`btn-remove-clearance-${idx}`} onClick={() => onRemove(idx)}
              className="text-muted-foreground hover:text-red-600 px-1">×</button>
      {c.ref_type === "component_edge" && (
        <select data-testid={`clearance-ref-id-${idx}`} className="luxe-input text-[11px] col-span-4"
                value={c.ref_id || ""}
                onChange={(e) => onUpdate(idx, { ref_id: e.target.value || null })}>
          <option value="">— Select component —</option>
          {siblings.map((s) => (
            <option key={s.id} value={s.id}>{displayName(s)}</option>
          ))}
        </select>
      )}
    </li>
  );
}


// ── Properties body: Position / Physical / Material / Sync ──────────
export function PropertiesBody(props) {
  const { selected, items, wallW, wallH, updateSelected } = props;
  const it = selected;
  const def = getComponent(it.type) || {};

  const setLeft = (v) => updateSelected({ x: Math.max(0, Math.min(wallW - it.width, v)) });
  const setFFL  = (v) => updateSelected({ y: Math.max(0, Math.min(wallH - it.height, v)) });
  const setRight = (v) => updateSelected({ x: Math.max(0, Math.min(wallW - it.width, wallW - v - it.width)) });
  const setWidth = (v) => updateSelected({ width: Math.max(1, Math.min(wallW - it.x, v)) });
  const rightMm = Math.max(0, wallW - it.x - it.width);

  return (
    <div className="space-y-3 text-sm min-w-0 overflow-visible">
      <PropsIdentity it={it} def={def} onLabel={(v) => updateSelected({ label: v })}/>
      <PositionPanel it={it} rightMm={rightMm}
                     onLeft={setLeft} onFFL={setFFL} onRight={setRight}
                     onWidth={setWidth}
                     onHeight={(v) => updateSelected({ height: Math.max(1, v) })}
                     onRotation={(v) => updateSelected({ rotation: v })}/>
      <PhysicalPanel it={it}
                     onZ={(v) => updateSelected({ z: v })}
                     onDepth={(v) => updateSelected({ depth: v })}/>
      {it.type === "tv" && (
        <TvDiagonalPanel it={it}
                         onDiagonal={(d) => {
                           const { w, h } = wxhFromDiagonal(d);
                           updateSelected({ diagonal_inches: d, width: w, height: h });
                         }}/>
      )}
      <HostPanel it={it} items={items} onHost={(v) => updateSelected({ host_id: v })}/>
      {isCabinetComponent(it.type) && (
        <CabinetConfigPanel it={it}
                            projectId={props.projectId}
                            onConfig={(cfg) => updateSelected({ cabinet_config: cfg })}/>
      )}
      {isCabinetComponent(it.type) && (
        <EndProfilePanel it={it}
                         onProfiles={(lp, rp) => updateSelected({
                           left_end_profile:  lp,
                           right_end_profile: rp,
                         })}/>
      )}
      {it.type === "fireplace" && (
        <FireplaceConfigPanel it={it}
                              onConfig={(cfg) => updateSelected({ fireplace_config: cfg })}/>
      )}
      {itemHasClearances(it) && (
        <ClearancePanel it={it}
                         onConfig={(cfg) => updateSelected({ clearance_config: cfg })}/>
      )}
      {it.type === "led_strip" && (
        <LedModePanel it={it} onMode={(m) => updateSelected({ attachment: attachmentForMode(m) })}/>
      )}
      {LIGHTING_TYPES[it.type] && (
        <LightingPropertiesPanel it={it}
                                 onChange={(next) => updateSelected({ lighting: next })}/>
      )}
      {SUBSTRATE_APPLICABLE.has(it.type) && (
        <SubstratePanel it={it} onSubstrate={(v) => updateSelected({ substrate: v })}/>
      )}
      {def.is_symbol && (
        <SymbolExtrasPanel it={it}
                           onMountHeight={(v) => updateSelected({ mounting_height_mm: v, y: v })}
                           onCircuit={(v) => updateSelected({ circuit: v })}/>
      )}
      <AlignLayerPanel {...props}/>
      {/* Legacy per-ref-type ClearancesEditor is only rendered when the
          item does NOT expose the V4 canonical clearance_config panel.
          Prevents the two panels stacking on top of each other on TV /
          soundbar / fireplace items (root cause of the "Clearance
          overlap" user report in iter38). */}
      {!itemHasClearances(it) && (
        <ClearancesEditor
          item={it}
          siblings={items.filter((i) => i.id !== it.id)}
          onChange={(cs) => updateSelected({ clearances: cs })}
        />
      )}
      <MaterialSection it={it} projectId={props.projectId}
                       onAssignMaterial={props.onAssignMaterial}
                       onUpdate={updateSelected}/>
      <QuantitySyncSection it={it} onDoSync={props.onDoSync} onSyncItem={props.onSyncItem}
                            onQtyMode={(v) => updateSelected({ quantity_mode: v })}
                            onWastage={(v) => updateSelected({ wastage_pct: v })}/>
    </div>
  );
}

function PropsIdentity({ it, def, onLabel }) {
  return (
    <div>
      <label className="overline text-[10px] block mb-1">Display name</label>
      <input data-testid="prop-label" className="luxe-input"
             value={it.label || ""} placeholder={def.label || it.type}
             onChange={(e) => onLabel(e.target.value)}/>
    </div>
  );
}

function PositionPanel({ it, rightMm, onLeft, onFFL, onRight, onWidth, onHeight, onRotation }) {
  return (
    <div className="pt-2 border-t border-border" data-testid="position-panel">
      <div className="overline text-[10px] mb-2">Position (mm)</div>
      <div className="grid grid-cols-2 gap-2">
        <NumField testid="prop-left"   label="Left"    value={Math.round(it.x)}      onChange={onLeft}/>
        <NumField testid="prop-ffl"    label="FFL"     value={Math.round(it.y)}      onChange={onFFL}/>
        <NumField testid="prop-right"  label="Right"   value={Math.round(rightMm)}   onChange={onRight}/>
        <NumField testid="prop-width"  label="Width"   value={Math.round(it.width)}  onChange={onWidth}/>
        <NumField testid="prop-height" label="Height"  value={Math.round(it.height)} onChange={onHeight}/>
        <NumField testid="prop-rotation" label="Rot (°)" value={it.rotation ?? 0}     onChange={onRotation}/>
      </div>
      <div className="text-[10px] text-muted-foreground mt-1">
        Left = distance from left wall · FFL = distance from finished floor · Right = wall − Left − Width
      </div>
    </div>
  );
}

function PhysicalPanel({ it, onZ, onDepth }) {
  return (
    <div className="pt-3 border-t border-border" data-testid="physical-panel">
      <div className="overline text-[10px] mb-2">Physical</div>
      <div className="grid grid-cols-2 gap-2">
        <NumField testid="prop-projection" label="Proj." value={it.z ?? 0}     onChange={onZ}/>
        <NumField testid="prop-depth"      label="Depth"      value={it.depth ?? 0} onChange={onDepth}/>
      </div>
      <div className="text-[10px] text-muted-foreground mt-1">
        Projection = distance from wall face (+Z toward room) · separate from 2D visual order.
      </div>
    </div>
  );
}

function TvDiagonalPanel({ it, onDiagonal }) {
  return (
    <div className="pt-3 border-t border-border" data-testid="tv-diagonal-panel">
      <label className="overline text-[10px] block mb-1">Diagonal size (inches)</label>
      <div className="flex items-center gap-2">
        <DraftNumInput testid="prop-tv-diagonal"
                       value={it.diagonal_inches ?? deriveDiagonalIn(it.width, it.height)}
                       min={1}
                       onCommit={(v) => onDiagonal(v)}/>
        <span className="text-[10px] text-muted-foreground">
          ≈ {Math.round(it.width)} × {Math.round(it.height)} mm
        </span>
      </div>
      <div className="text-[10px] text-muted-foreground mt-1">
        {it.product_id
          ? "Product assigned — manufacturer dimensions authoritative."
          : "Generic 16:9 — assign a specific TV to override."}
      </div>
    </div>
  );
}

function HostPanel({ it, items, onHost }) {
  return (
    <div className="pt-3 border-t border-border" data-testid="host-panel">
      <label className="overline text-[10px] block mb-1">Host / Parent</label>
      <select data-testid="prop-host-id" className="luxe-input"
              value={it.host_id || ""}
              onChange={(e) => onHost(e.target.value || null)}>
        <option value="">— None (wall) —</option>
        {items.filter((i) => i.id !== it.id).map((i) => (
          <option key={i.id} value={i.id}>{displayName(i)}</option>
        ))}
      </select>
    </div>
  );
}

function LedModePanel({ it, onMode }) {
  return (
    <div className="pt-3 border-t border-border" data-testid="led-mode-panel">
      <label className="overline text-[10px] block mb-1">LED Mode</label>
      <select data-testid="prop-led-mode" className="luxe-input"
              value={ledModeFor(it)}
              onChange={(e) => onMode(e.target.value)}>
        {LED_PERIMETER_MODES.map((m) => (
          <option key={m.value} value={m.value}>{m.label}</option>
        ))}
      </select>
      {it.host_id && it.attachment?.mode === "perimeter" && (
        <div className="text-[10px] text-muted-foreground mt-1" data-testid="led-derived-note">
          Length derives from host geometry.
        </div>
      )}
    </div>
  );
}

function SubstratePanel({ it, onSubstrate }) {
  return (
    <div className="pt-3 border-t border-border" data-testid="substrate-panel">
      <label className="overline text-[10px] block mb-1">Substrate</label>
      <select data-testid="prop-substrate" className="luxe-input"
              value={it.substrate || "plasterboard_new"}
              onChange={(e) => onSubstrate(e.target.value)}>
        {SUBSTRATE_OPTIONS.map((s) => (
          <option key={s.value} value={s.value}>{s.label}</option>
        ))}
      </select>
    </div>
  );
}

// ── Cabinet Configuration Panel ───────────────────────────────────
// Canonical cabinet configuration: bays (each with kind door|drawer|open),
// per-bay drawer count / shelves / hardware, layout_mode (even_split |
// custom_split | mixed), and denormalised door_count / drawer_count for
// convenience. All fields are stored under `cabinet_config` on the item.
// This panel is the SINGLE source of truth for cabinet configuration
// across 2D representation, pricing quantities, future 3D and future
// construction docs.
// A bay is "meaningful" when it carries explicit user configuration that
// would be destroyed by silently reducing the bay count. Defaults such
// as kind='door', drawer_count=2 for drawer bays, hardware='handleless',
// and shelves=0 are treated as auto-generated / non-meaningful.
export function isBayMeaningful(bay) {
  if (!bay) return false;
  if (bay.kind && bay.kind !== "door") return true;
  if (bay.hinge && bay.hinge !== "default" && bay.hinge !== "left") return true;
  if ((bay.shelves ?? 0) > 0) return true;
  if (bay.hardware && bay.hardware !== "handleless") return true;
  if (bay.drawer_count !== undefined && bay.drawer_count !== null
      && bay.drawer_count !== 2 && bay.drawer_count !== 0) return true;
  if (bay.material_id) return true;
  if (bay.material_slots && Object.keys(bay.material_slots).length > 0) return true;
  if (bay.front_overrides && bay.front_overrides.some((f) => f && (f.material_id || f.hinge))) return true;
  return false;
}

// ── End Profile Panel (Phase 2B-0 canonical curved geometry) ────
// Canonical Square / Radius / Semicircle / Bevel end profiles for the
// LEFT and RIGHT ends of a Cabinet. Values persist on
// `it.left_end_profile` and `it.right_end_profile`. Geometry is
// completely independent of materials/lighting/pricing — changing an
// end profile updates scene_hash and derived quantities only.
const END_PROFILE_KINDS = [
  { v: "square",     l: "Square"     },
  { v: "radius",     l: "Radius"     },
  { v: "semicircle", l: "Semicircle" },
  { v: "bevel",      l: "Bevel"      },
];
function EndProfilePanel({ it, onProfiles }) {
  const lp = it.left_end_profile  || { kind: "square" };
  const rp = it.right_end_profile || { kind: "square" };
  const editSide = (side, patch) => {
    const cur = side === "left" ? lp : rp;
    const next = { ...cur, ...patch };
    // Drop parameter fields that don't belong to the new kind so we
    // never persist stale radius_mm on a Square profile etc.
    if (next.kind !== "radius") delete next.radius_mm;
    if (next.kind !== "bevel")  delete next.bevel_depth_mm;
    const nextLeft  = side === "left"  ? next : lp;
    const nextRight = side === "right" ? next : rp;
    onProfiles(
      nextLeft.kind === "square"  ? null : nextLeft,
      nextRight.kind === "square" ? null : nextRight,
    );
  };
  const renderSide = (side, p) => (
    <div className="min-w-0" data-testid={`end-profile-${side}`}>
      <label className="overline text-[10px] block mb-1 capitalize">{side} end</label>
      <select data-testid={`end-profile-${side}-kind`}
              className="luxe-input w-full min-w-0"
              value={p.kind || "square"}
              onChange={(e) => editSide(side, { kind: e.target.value })}>
        {END_PROFILE_KINDS.map((k) => <option key={k.v} value={k.v}>{k.l}</option>)}
      </select>
      {p.kind === "radius" && (
        <div className="mt-1">
          <label className="overline text-[10px] block mb-1">Radius (mm)</label>
          <DraftNumInput testid={`end-profile-${side}-radius`}
                         value={Number(p.radius_mm) || 0}
                         min={1}
                         onCommit={(v) => editSide(side, { kind: "radius", radius_mm: v })}/>
        </div>
      )}
      {p.kind === "bevel" && (
        <div className="mt-1">
          <label className="overline text-[10px] block mb-1">Bevel depth (mm)</label>
          <DraftNumInput testid={`end-profile-${side}-bevel-depth`}
                         value={Number(p.bevel_depth_mm) || 0}
                         min={1}
                         onCommit={(v) => editSide(side, { kind: "bevel", bevel_depth_mm: v })}/>
        </div>
      )}
    </div>
  );
  return (
    <div className="pt-3 border-t border-border" data-testid="end-profile-panel">
      <div className="overline text-[10px] mb-2">End profiles</div>
      <div className="text-[10px] text-muted-foreground mb-2">
        Canonical curved geometry. Independent of material — feeds 2D, quantities, 3D and future renders.
      </div>
      <div className="grid grid-cols-2 gap-2 min-w-0">
        {renderSide("left",  lp)}
        {renderSide("right", rp)}
      </div>
    </div>
  );
}


function CabinetConfigPanel({ it, projectId, onConfig }) {
  const cfg = it.cabinet_config || { layout_mode: "even_split", bays: [], door_count: 2, drawer_count: 0 };
  const bays = cfg.bays || [];
  // Temporary edit state for the Bay Count field. UI-only — canonical
  // cabinet_config.bays.length is untouched while the user is typing.
  const [bayCountDraft, setBayCountDraft] = useState(String(bays.length || 0));
  const [pendingReduce, setPendingReduce] = useState(null); // { fromN, toN }
  const bayCountInputRef = useRef(null);
  // Keep the draft in sync with canonical state when the selected item
  // changes or an external mutation (undo/redo, restore) rewrites bays.
  useEffect(() => {
    setBayCountDraft(String(bays.length || 0));
  }, [it.id, bays.length]);
  const setLayout = (mode) => {
    const next = { ...cfg, layout_mode: mode };
    // Even-split → deterministic integer allocation. Remainder mm are
    // distributed LEFT→RIGHT so sum(widths) == cabinet.width exactly.
    if (mode === "even_split" && bays.length > 0) {
      const usable = Math.max(0, Math.round(it.width || 0));
      const n = bays.length;
      const base = Math.floor(usable / n);
      const rem = usable - base * n;
      next.bays = bays.map((b, i) => ({ ...b, width_mm: base + (i < rem ? 1 : 0) }));
    }
    onConfig(next);
  };
  // Compute the next bay list for a target N without side effects.
  const nextBaysForCount = (n) => {
    const usable = Math.max(0, Math.round(it.width || 0));
    const base = Math.floor(usable / n);
    const rem = usable - base * n;
    const evenWidths = Array.from({ length: n }, (_, i) => base + (i < rem ? 1 : 0));
    const currentBays = bays.length ? bays : [{ id: "b1", kind: "door" }];
    return Array.from({ length: n }, (_, i) => {
      const existing = currentBays[i] || { kind: "door" };
      return {
        id: existing.id || `b${i + 1}`,
        kind: existing.kind || "door",
        width_mm: cfg.layout_mode === "custom_split" && existing.width_mm
          ? existing.width_mm : evenWidths[i],
        drawer_count: existing.drawer_count ?? (existing.kind === "drawer" ? 2 : 0),
        shelves: existing.shelves ?? 0,
        hardware: existing.hardware || "handleless",
        ...(existing.hinge ? { hinge: existing.hinge } : {}),
        ...(existing.material_id ? { material_id: existing.material_id } : {}),
        ...(existing.material_slots ? { material_slots: existing.material_slots } : {}),
        ...(existing.front_overrides ? { front_overrides: existing.front_overrides } : {}),
      };
    });
  };
  const commitBayCount = (n) => {
    const nextBays = nextBaysForCount(n);
    onConfig({
      ...cfg, bays: nextBays,
      door_count:  nextBays.filter((b) => b.kind === "door").length,
      drawer_count: nextBays.reduce((s, b) => s + (b.kind === "drawer" ? (b.drawer_count || 1) : 0), 0),
    });
  };
  // Public entry point — validates n, guards destructive reduction, and
  // commits when safe. Called from stepper buttons and the blur/Enter
  // commit handler.
  const requestBayCount = (nRaw) => {
    const n = Math.max(1, Math.min(12, Math.floor(Number(nRaw)) || 0));
    if (n <= 0 || Number.isNaN(n)) {
      // Restore to previous valid canonical value.
      setBayCountDraft(String(bays.length || 0));
      return;
    }
    if (n === bays.length) {
      setBayCountDraft(String(n));
      return;
    }
    if (n < bays.length) {
      const dropped = bays.slice(n);
      const droppedMeaningful = dropped.filter(isBayMeaningful);
      if (droppedMeaningful.length > 0) {
        setPendingReduce({ fromN: bays.length, toN: n, dropped });
        // Keep draft showing the user's intent until they confirm/cancel.
        setBayCountDraft(String(n));
        return;
      }
    }
    commitBayCount(n);
    setBayCountDraft(String(n));
  };
  const stepDown = () => {
    if (bays.length <= 1) return;
    requestBayCount(bays.length - 1);
  };
  const stepUp = () => {
    if (bays.length >= 12) return;
    requestBayCount(bays.length + 1);
  };
  const onDraftBlur = () => {
    if (bayCountDraft === "" || bayCountDraft === null || bayCountDraft === undefined) {
      // Empty/invalid → restore previous canonical value.
      setBayCountDraft(String(bays.length || 0));
      return;
    }
    const parsed = parseInt(bayCountDraft, 10);
    if (Number.isNaN(parsed) || parsed <= 0) {
      setBayCountDraft(String(bays.length || 0));
      return;
    }
    requestBayCount(parsed);
  };
  const onDraftKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onDraftBlur();
      bayCountInputRef.current?.blur();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setBayCountDraft(String(bays.length || 0));
      bayCountInputRef.current?.blur();
    }
  };
  const confirmReduce = () => {
    if (!pendingReduce) return;
    commitBayCount(pendingReduce.toN);
    setBayCountDraft(String(pendingReduce.toN));
    setPendingReduce(null);
  };
  const cancelReduce = () => {
    if (!pendingReduce) return;
    setBayCountDraft(String(pendingReduce.fromN));
    setPendingReduce(null);
  };
  const setBay = (idx, patch) => {
    const nextBays = bays.map((b, i) => i === idx ? { ...b, ...patch } : b);
    onConfig({
      ...cfg, bays: nextBays,
      door_count:   nextBays.filter((b) => b.kind === "door").length,
      drawer_count: nextBays.reduce((s, b) => s + (b.kind === "drawer" ? (b.drawer_count || 1) : 0), 0),
    });
  };
  // Move a bay left or right (iPad-friendly reorder without drag).
  const moveBay = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= bays.length) return;
    const nextBays = [...bays];
    [nextBays[idx], nextBays[j]] = [nextBays[j], nextBays[idx]];
    onConfig({ ...cfg, bays: nextBays });
  };
  return (
    <div className="pt-3 border-t border-border" data-testid="cabinet-config">
      <div className="overline text-[10px] mb-2">Cabinet configuration</div>
      {/* Canonical live preview — SAME renderer used by the 2D scene
          for cabinet fronts, so the studio sees the actual proportions
          the canvas will draw. */}
      <div className="mb-3 p-2 border border-border bg-muted/30">
        <CabinetBayPreview
          cabinetConfig={cfg}
          cabinetWidthMm={it.width || 0}
          cabinetHeightMm={it.height || 0}
          leftEndProfile={it.left_end_profile || null}
          rightEndProfile={it.right_end_profile || null}
          pxWidth={196}
          pxHeight={80}
          testid="cab-bay-preview"/>
      </div>
      <div className="mb-2">
        <label className="overline text-[10px] block mb-1">Layout</label>
        <select data-testid="cab-layout-mode" className="luxe-input w-full"
                value={cfg.layout_mode || "even_split"}
                onChange={(e) => setLayout(e.target.value)}>
          {CABINET_LAYOUT_MODES.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
        </select>
      </div>
      <div className="mb-2 min-w-0">
        <label className="overline text-[10px] block mb-1">Number of Bays</label>
        <div className="grid items-stretch w-full min-w-0"
             style={{ gridTemplateColumns: "30px minmax(48px, 1fr) 30px", gap: "6px" }}
             data-testid="cab-bay-count-group">
          <button type="button"
                  data-testid="cab-bay-count-minus"
                  onClick={stepDown}
                  disabled={bays.length <= 1}
                  aria-label="Decrease number of bays"
                  title="Decrease number of bays"
                  className="h-9 inline-flex items-center justify-center border border-border hover:border-foreground disabled:opacity-30 disabled:cursor-not-allowed">
            <Minus className="w-3.5 h-3.5" strokeWidth={1.75}/>
          </button>
          <input data-testid="cab-bay-count"
                 ref={bayCountInputRef}
                 type="text"
                 inputMode="numeric"
                 pattern="[0-9]*"
                 aria-label="Number of bays"
                 className="luxe-input h-9 w-full min-w-0 text-center px-2 no-spinner"
                 value={bayCountDraft}
                 onFocus={(e) => { try { e.currentTarget.select(); } catch (_) {} }}
                 onChange={(e) => setBayCountDraft(e.target.value.replace(/[^0-9]/g, ""))}
                 onBlur={onDraftBlur}
                 onKeyDown={onDraftKeyDown}/>
          <button type="button"
                  data-testid="cab-bay-count-plus"
                  onClick={stepUp}
                  disabled={bays.length >= 12}
                  aria-label="Increase number of bays"
                  title="Increase number of bays"
                  className="h-9 inline-flex items-center justify-center border border-border hover:border-foreground disabled:opacity-30 disabled:cursor-not-allowed">
            <Plus className="w-3.5 h-3.5" strokeWidth={1.75}/>
          </button>
        </div>
      </div>
      {pendingReduce && (
        <div data-testid="cab-bay-reduce-confirm"
             className="mb-3 p-2 border border-destructive/60 bg-destructive/5">
          <div className="text-[11px] mb-2">
            Bays {pendingReduce.toN + 1}
            {pendingReduce.fromN > pendingReduce.toN + 1 ? `–${pendingReduce.fromN}` : ""} contain
            configured information that will be removed.
          </div>
          <div className="flex gap-2">
            <button type="button"
                    data-testid="cab-bay-reduce-cancel"
                    onClick={cancelReduce}
                    className="flex-1 h-8 border border-border text-[11px] hover:border-foreground">
              Cancel
            </button>
            <button type="button"
                    data-testid="cab-bay-reduce-confirm-btn"
                    onClick={confirmReduce}
                    className="flex-1 h-8 border border-destructive bg-destructive text-destructive-foreground text-[11px]">
              Delete Bays
            </button>
          </div>
        </div>
      )}
      <div className="mb-2">
        <label className="overline text-[10px] block mb-1">Snap (visual drag)</label>
        <select data-testid="cab-snap-mm" className="luxe-input"
                value={cfg.bay_snap_mm ?? 25}
                onChange={(e) => onConfig({ ...cfg, bay_snap_mm: parseInt(e.target.value, 10) || 0 })}>
          {CABINET_SNAP_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
        </select>
        <div className="text-[10px] text-muted-foreground mt-1">
          Numeric entry always accepts exact mm; snap only applies to visual adjustment.
        </div>
      </div>
      <div className="text-[10px] text-muted-foreground mb-2 flex gap-3">
        <span data-testid="cab-door-count">Doors: {cfg.door_count ?? 0}</span>
        <span data-testid="cab-drawer-count">Drawers: {cfg.drawer_count ?? 0}</span>
      </div>
      <div className="space-y-2">
        {bays.map((b, i) => (
          <div key={b.id || i}
               data-testid={`cab-bay-${i}`}
               className="p-2 border border-border space-y-2">
            <div className="text-[10px] text-muted-foreground flex items-center gap-1">
              <span className="flex-1">Bay {i + 1}</span>
              <button type="button"
                      data-testid={`cab-bay-${i}-move-left`}
                      onClick={() => moveBay(i, -1)}
                      disabled={i === 0}
                      aria-label={`Move bay ${i + 1} left`}
                      title="Move left"
                      className="w-6 h-6 inline-flex items-center justify-center border border-border hover:border-foreground disabled:opacity-30">
                <ChevronLeft className="w-3 h-3" strokeWidth={1.5}/>
              </button>
              <button type="button"
                      data-testid={`cab-bay-${i}-move-right`}
                      onClick={() => moveBay(i, 1)}
                      disabled={i === bays.length - 1}
                      aria-label={`Move bay ${i + 1} right`}
                      title="Move right"
                      className="w-6 h-6 inline-flex items-center justify-center border border-border hover:border-foreground disabled:opacity-30">
                <ChevronRight className="w-3 h-3" strokeWidth={1.5}/>
              </button>
            </div>
            {/* Full-width Bay Type — readable at every viewport. */}
            <div>
              <label className="overline text-[10px] block mb-1">Bay Type</label>
              <select data-testid={`cab-bay-${i}-kind`}
                      className="luxe-input w-full"
                      value={b.kind || "door"}
                      onChange={(e) => setBay(i, { kind: e.target.value })}>
                {CABINET_BAY_KINDS.map((k) => <option key={k.v} value={k.v}>{k.l}</option>)}
              </select>
            </div>
            {b.kind === "door" && (
              <div>
                <label className="overline text-[10px] block mb-1">Door opening</label>
                <HingeSegmented
                  value={b.hinge || "right"}
                  onChange={(v) => setBay(i, { hinge: v })}
                  bayIndex={i}/>
              </div>
            )}
            {b.kind === "drawer" && (
              <DrawerLayoutEditor
                bay={b}
                bayIndex={i}
                cabinetHeightMm={Number(it.height) || 0}
                componentType={it.type}
                projectId={projectId}
                onSetBay={(patch) => setBay(i, patch)}/>
            )}
            {b.kind === "open" && (
              <ShelfLayoutEditor
                bay={b}
                bayIndex={i}
                cabinetHeightMm={Number(it.height) || 0}
                componentType={it.type}
                projectId={projectId}
                onSetBay={(patch) => setBay(i, patch)}/>
            )}
            <div>
              <label className="overline text-[10px] block mb-1">Width (mm)</label>
              {cfg.layout_mode === "even_split" ? (
                <input data-testid={`cab-bay-${i}-width`}
                       className="luxe-input w-full opacity-60"
                       disabled
                       value={b.width_mm ?? 0}
                       readOnly/>
              ) : (
                <DraftNumInput testid={`cab-bay-${i}-width`}
                               value={b.width_mm ?? 0}
                               min={0}
                               onCommit={(v) => setBay(i, { width_mm: v })}/>
              )}
            </div>
            {/* Per-bay material override (inheritance-first). Default
                OFF → bay inherits the Cabinet Front slot material.
                When ON, the bay stores its own material_id override
                (or open/back/shelf materials for open bays). Reuses
                the canonical MaterialPicker via material_id — no
                parallel cabinet material system. */}
            <div className="mt-1">
              <label className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                <input type="checkbox"
                       data-testid={`cab-bay-${i}-material-override`}
                       checked={!!b.material_override}
                       onChange={(e) => setBay(i, {
                         material_override: e.target.checked,
                         ...(e.target.checked ? {} : {
                           material_id: null, back_material_id: null,
                           shelf_material_id: null, interior_material_id: null,
                         }),
                       })}/>
                Override cabinet default material
              </label>
              {b.material_override && (
                <div className="mt-1 space-y-2 pl-2 border-l-2 border-border">
                  {(b.kind === "door" || b.kind === "drawer") && (
                    <div data-testid={`cab-bay-${i}-front-material`} className="min-w-0">
                      <div className="text-[11px] font-medium mb-1">
                        {b.kind === "drawer" ? "Drawer Front Material" : "Door Front Material"}
                      </div>
                      <MaterialPicker
                        componentType={it.type}
                        slotKey="front"
                        projectId={projectId}
                        currentId={b.material_id || ""}
                        onPick={(p) => setBay(i, { material_id: p.id })}
                        onClear={() => setBay(i, { material_id: null })}
                        compact/>
                    </div>
                  )}
                  {b.kind === "open" && (
                    <>
                      <div data-testid={`cab-bay-${i}-back-material`} className="min-w-0">
                        <div className="text-[11px] font-medium mb-1">Back Panel Material</div>
                        <MaterialPicker
                          componentType={it.type}
                          slotKey="carcass"
                          projectId={projectId}
                          currentId={b.back_material_id || ""}
                          onPick={(p) => setBay(i, { back_material_id: p.id })}
                          onClear={() => setBay(i, { back_material_id: null })}
                          compact/>
                      </div>
                      <div data-testid={`cab-bay-${i}-shelf-material`} className="min-w-0">
                        <div className="text-[11px] font-medium mb-1">Shelf Material</div>
                        <MaterialPicker
                          componentType={it.type}
                          slotKey="top"
                          projectId={projectId}
                          currentId={b.shelf_material_id || ""}
                          onPick={(p) => setBay(i, { shelf_material_id: p.id })}
                          onClear={() => setBay(i, { shelf_material_id: null })}
                          compact/>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Drawer Layout Editor (Equal / Custom, per V4 Part H) ───────────
// - Equal: legacy `drawer_count` + `drawer_layout='equal'`. Heights
//   are computed by the renderer.
// - Custom: canonical `drawers: [{id, height_mm}]` with stable IDs.
//   Existing heights are preserved when count changes (Part H2 rule):
//   never silently redistribute or reset to Equal.
// - Validation compares sum(heights) against usable bay height (V4
//   directive: usable_drawer_height = canonical bay height; no invented
//   gap/reveal until modelled canonically).
function _newDrawerId(existing) {
  const ids = new Set((existing || []).map((d) => d && d.id).filter(Boolean));
  let n = (existing || []).length + 1;
  while (ids.has(`d${n}`)) n += 1;
  return `d${n}`;
}
function DrawerLayoutEditor({ bay, bayIndex, cabinetHeightMm,
                              componentType, projectId, onSetBay }) {
  const layout = bay.drawer_layout || (Array.isArray(bay.drawers) && bay.drawers.length > 0 ? "custom" : "equal");
  const drawers = Array.isArray(bay.drawers) ? bay.drawers : [];
  const usableMm = Math.max(0, Math.round(cabinetHeightMm || 0));
  const setLayout = (mode) => {
    if (mode === "equal") {
      onSetBay({ drawer_layout: "equal" });
      return;
    }
    // Switching to custom seeds an even split from current drawer_count
    // so the studio has valid starting values. Preserves existing
    // custom drawers if any.
    if (drawers.length > 0) {
      onSetBay({ drawer_layout: "custom" });
      return;
    }
    const n = Math.max(1, Number(bay.drawer_count) || 1);
    const base = usableMm > 0 ? Math.floor(usableMm / n) : 0;
    const rem = usableMm > 0 ? usableMm - base * n : 0;
    const seed = Array.from({ length: n }, (_, i) => ({
      id: `d${i + 1}`,
      height_mm: base + (i < rem ? 1 : 0),
    }));
    onSetBay({ drawer_layout: "custom", drawers: seed, drawer_count: n });
  };
  const setDrawerHeight = (idx, mmRaw) => {
    const mm = Math.max(0, Math.floor(Number(mmRaw) || 0));
    const next = drawers.map((d, i) => i === idx ? { ...d, height_mm: mm } : d);
    onSetBay({ drawers: next });
  };
  // Per-drawer Material override (Stage C). Overrides walk the closest-
  // explicit-wins hierarchy: Cabinet Front → Drawer Stack (bay) → Drawer
  // Front (drawer.material_id). Setting a drawer material NEVER mutates
  // canonical drawer_height. Stable drawer.id preserved.
  const setDrawerMaterial = (idx, mid) => {
    const next = drawers.map((d, i) => i === idx
      ? { ...d, ...(mid ? { material_id: mid } : { material_id: null }) }
      : d);
    onSetBay({ drawers: next });
  };
  const addDrawer = () => {
    // Preserve existing heights (V4 Part H2). New drawer consumes any
    // positive unallocated height; otherwise inserted with an explicit
    // `incomplete` flag so it renders visibly invalid until Studio
    // resolves it — never commit a fake 0mm valid drawer.
    const sum = drawers.reduce((s, d) => s + (Number(d.height_mm) || 0), 0);
    const remaining = usableMm - sum;
    const id = _newDrawerId(drawers);
    const newDrawer = remaining > 0
      ? { id, height_mm: remaining }
      : { id, height_mm: 0, incomplete: true };
    onSetBay({
      drawers: [...drawers, newDrawer],
      drawer_count: (drawers.length + 1),
    });
  };
  const removeDrawer = (idx) => {
    // Meaningful-data protection: if this drawer has explicit
    // material/hardware, surface a confirm via window.confirm — kept
    // simple for the closure gate; undo restores.
    const d = drawers[idx];
    const isMeaningful = d && (d.material_id || (d.hardware && d.hardware !== "handleless"));
    if (isMeaningful && !window.confirm(`Drawer ${idx + 1} has configured information that will be removed. Continue?`)) {
      return;
    }
    const next = drawers.filter((_, i) => i !== idx);
    onSetBay({ drawers: next, drawer_count: Math.max(1, next.length) });
  };
  const totalMm = drawers.reduce((s, d) => s + (Number(d.height_mm) || 0), 0);
  const diff = usableMm - totalMm;
  const hasIncomplete = drawers.some((d) => d && d.incomplete);
  return (
    <div className="space-y-2" data-testid={`cab-bay-${bayIndex}-drawer-editor`}>
      <div>
        <label className="overline text-[10px] block mb-1">Drawer layout</label>
        <div className="grid grid-cols-2 gap-1"
             data-testid={`cab-bay-${bayIndex}-drawer-layout`}>
          <button type="button"
                  data-testid={`cab-bay-${bayIndex}-drawer-layout-equal`}
                  onClick={() => setLayout("equal")}
                  aria-pressed={layout === "equal"}
                  className={`h-8 text-[11px] border ${layout === "equal" ? "border-foreground bg-foreground/10" : "border-border hover:border-foreground/60"}`}>
            Equal
          </button>
          <button type="button"
                  data-testid={`cab-bay-${bayIndex}-drawer-layout-custom`}
                  onClick={() => setLayout("custom")}
                  aria-pressed={layout === "custom"}
                  className={`h-8 text-[11px] border ${layout === "custom" ? "border-foreground bg-foreground/10" : "border-border hover:border-foreground/60"}`}>
            Custom
          </button>
        </div>
      </div>
      {layout === "equal" ? (
        <div>
          <label className="overline text-[10px] block mb-1">Drawer count</label>
          <DraftNumInput testid={`cab-bay-${bayIndex}-drawer-count`}
                         value={bay.drawer_count || 1}
                         min={1} max={8}
                         onCommit={(v) => onSetBay({ drawer_count: v })}/>
        </div>
      ) : (
        <div className="space-y-1" data-testid={`cab-bay-${bayIndex}-drawer-custom`}>
          <div className="overline text-[10px]">Drawer heights (mm)</div>
          {drawers.map((d, di) => (
            <div key={d.id || di}
                 className="border border-transparent hover:border-border/40 px-1 py-0.5">
              <div className="flex items-center gap-1">
                <span className="text-[10px] text-muted-foreground w-16">
                  Drawer {di + 1}
                </span>
                <DraftIntInput
                  testid={`cab-bay-${bayIndex}-drawer-${di}-height`}
                  value={Number(d.height_mm) || 0}
                  onCommit={(mm) => setDrawerHeight(di, mm)}
                  min={0}/>
                <button type="button"
                        data-testid={`cab-bay-${bayIndex}-drawer-${di}-remove`}
                        onClick={() => removeDrawer(di)}
                        aria-label={`Remove drawer ${di + 1}`}
                        title="Remove drawer"
                        disabled={drawers.length <= 1}
                        className="w-7 h-7 inline-flex items-center justify-center border border-border hover:border-foreground disabled:opacity-30">
                  <Minus className="w-3 h-3"/>
                </button>
                {d.incomplete && (
                  <span className="text-[9px] text-destructive" title="Height not yet assigned">!</span>
                )}
              </div>
              {/* Per-drawer front material override (Stage C). Empty
                  ⇒ drawer inherits the Drawer Stack (bay) material,
                  which itself inherits the Cabinet Front slot. Setting
                  a material NEVER mutates drawer height. */}
              <div className="pl-16 mt-0.5 min-w-0"
                   data-testid={`cab-bay-${bayIndex}-drawer-${di}-material`}>
                <MaterialPicker
                  componentType={componentType}
                  slotKey="front"
                  projectId={projectId}
                  currentId={d.material_id || ""}
                  onPick={(p) => setDrawerMaterial(di, p.id)}
                  onClear={() => setDrawerMaterial(di, null)}
                  compact
                  placeholder="Inherit drawer stack material"/>
              </div>
            </div>
          ))}
          <button type="button"
                  data-testid={`cab-bay-${bayIndex}-drawer-add`}
                  onClick={addDrawer}
                  disabled={drawers.length >= 8}
                  className="w-full h-8 text-[11px] border border-border hover:border-foreground disabled:opacity-30">
            + Add drawer
          </button>
          <div className="text-[10px] mt-1"
               data-testid={`cab-bay-${bayIndex}-drawer-validation`}>
            {usableMm <= 0 ? (
              <span className="text-muted-foreground">Set cabinet height to validate</span>
            ) : hasIncomplete ? (
              <span className="text-destructive">
                {drawers.filter((d) => d.incomplete).length} drawer height not yet assigned
              </span>
            ) : diff === 0 ? (
              <span className="text-emerald-600">Total {totalMm} mm — matches bay height</span>
            ) : diff > 0 ? (
              <span className="text-destructive">{diff} mm unallocated (bay height {usableMm} mm)</span>
            ) : (
              <span className="text-destructive">{-diff} mm over (bay height {usableMm} mm)</span>
            )}
          </div>
          <div className="text-[9px] text-muted-foreground italic">
            Drawer front gap/reveal geometry is not yet canonical; validation uses raw bay height.
          </div>
        </div>
      )}
    </div>
  );
}

// ── Shelf Layout Editor (Equal / Custom, per V4 Part I) ────────────
// - Equal: legacy `shelves` int + `shelf_layout='equal'`. Distribution
//   handled by the renderer.
// - Custom: canonical `shelves_config: [{id, y_mm}]` where y_mm is the
//   height from the bay interior bottom (Y-up). Stable IDs preserved
//   across re-order (visual sort ascending is view-only per V4 3a).
// - Validation: shelves must lie inside 0 < y_mm < bay_height; duplicate
//   y_mm is flagged; the renderer draws thin horizontal strokes only
//   (V4 4a — no invented shelf thickness rectangle).
function _newShelfId(existing) {
  const ids = new Set((existing || []).map((s) => s && s.id).filter(Boolean));
  let n = (existing || []).length + 1;
  while (ids.has(`s${n}`)) n += 1;
  return `s${n}`;
}
function ShelfLayoutEditor({ bay, bayIndex, cabinetHeightMm,
                             componentType, projectId, onSetBay }) {
  const layout = bay.shelf_layout || (Array.isArray(bay.shelves_config) && bay.shelves_config.length > 0 ? "custom" : "equal");
  const shelves = Array.isArray(bay.shelves_config) ? bay.shelves_config : [];
  const usableMm = Math.max(0, Math.round(cabinetHeightMm || 0));
  const setLayout = (mode) => {
    if (mode === "equal") {
      onSetBay({ shelf_layout: "equal" });
      return;
    }
    if (shelves.length > 0) {
      onSetBay({ shelf_layout: "custom" });
      return;
    }
    const n = Math.max(0, Number(bay.shelves) || 0);
    if (n === 0 || usableMm === 0) {
      onSetBay({ shelf_layout: "custom", shelves_config: [] });
      return;
    }
    const step = usableMm / (n + 1);
    const seed = Array.from({ length: n }, (_, i) => ({
      id: `s${i + 1}`,
      y_mm: Math.round(step * (i + 1)),
    }));
    onSetBay({ shelf_layout: "custom", shelves_config: seed });
  };
  const setShelfY = (idx, yRaw) => {
    const y = Math.max(0, Math.floor(Number(yRaw) || 0));
    const next = shelves.map((s, i) => i === idx ? { ...s, y_mm: y } : s);
    onSetBay({ shelves_config: next });
  };
  // Per-shelf material override (Stage C). Stable shelves_config.id
  // carries both physical Y and material_id — same canonical record.
  // Changing shelf Y does NOT change material, and vice-versa. Empty
  // ⇒ shelf inherits Open Bay Shelf Default (bay.shelf_material_id)
  // then Cabinet Front slot.
  const setShelfMaterial = (idx, mid) => {
    const next = shelves.map((s, i) => i === idx
      ? { ...s, ...(mid ? { material_id: mid } : { material_id: null }) }
      : s);
    onSetBay({ shelves_config: next });
  };
  const addShelf = () => {
    const id = _newShelfId(shelves);
    const defaultY = shelves.length === 0 ? Math.max(1, Math.round(usableMm / 2)) : 0;
    onSetBay({ shelves_config: [...shelves, { id, y_mm: defaultY }] });
  };
  const removeShelf = (idx) => {
    const s = shelves[idx];
    const isMeaningful = s && (s.material_id);
    if (isMeaningful && !window.confirm(`Shelf ${idx + 1} has configured information that will be removed. Continue?`)) {
      return;
    }
    onSetBay({ shelves_config: shelves.filter((_, i) => i !== idx) });
  };
  // Validation: out-of-range + duplicates. Do NOT reorder canonical.
  const seen = new Map();
  const issues = shelves.map((s, i) => {
    const y = Number(s.y_mm) || 0;
    if (usableMm > 0 && (y <= 0 || y >= usableMm)) {
      return { i, kind: "range", msg: `Shelf ${i + 1} @ ${y}mm outside 0–${usableMm}mm` };
    }
    if (seen.has(y)) {
      return { i, kind: "dup", msg: `Shelf ${i + 1} duplicates position ${y}mm` };
    }
    seen.set(y, i);
    return null;
  }).filter(Boolean);
  return (
    <div className="space-y-2" data-testid={`cab-bay-${bayIndex}-shelf-editor`}>
      <div>
        <label className="overline text-[10px] block mb-1">Shelf spacing</label>
        <div className="grid grid-cols-2 gap-1"
             data-testid={`cab-bay-${bayIndex}-shelf-layout`}>
          <button type="button"
                  data-testid={`cab-bay-${bayIndex}-shelf-layout-equal`}
                  onClick={() => setLayout("equal")}
                  aria-pressed={layout === "equal"}
                  className={`h-8 text-[11px] border ${layout === "equal" ? "border-foreground bg-foreground/10" : "border-border hover:border-foreground/60"}`}>
            Equal
          </button>
          <button type="button"
                  data-testid={`cab-bay-${bayIndex}-shelf-layout-custom`}
                  onClick={() => setLayout("custom")}
                  aria-pressed={layout === "custom"}
                  className={`h-8 text-[11px] border ${layout === "custom" ? "border-foreground bg-foreground/10" : "border-border hover:border-foreground/60"}`}>
            Custom
          </button>
        </div>
      </div>
      {layout === "equal" ? (
        <div>
          <label className="overline text-[10px] block mb-1">Shelves</label>
          <DraftNumInput testid={`cab-bay-${bayIndex}-shelves`}
                         value={bay.shelves || 0}
                         min={0} max={8}
                         onCommit={(v) => onSetBay({ shelves: v })}/>
        </div>
      ) : (
        <div className="space-y-1" data-testid={`cab-bay-${bayIndex}-shelf-custom`}>
          <div className="overline text-[10px]">
            Shelf positions — mm from bay bottom
          </div>
          {shelves.length === 0 && (
            <div className="text-[10px] text-muted-foreground italic">
              No shelves — click Add shelf.
            </div>
          )}
          {shelves.map((s, si) => (
            <div key={s.id || si}
                 className="border border-transparent hover:border-border/40 px-1 py-0.5">
              <div className="flex items-center gap-1">
                <span className="text-[10px] text-muted-foreground w-16">
                  Shelf {si + 1}
                </span>
                <DraftIntInput
                  testid={`cab-bay-${bayIndex}-shelf-${si}-y`}
                  value={Number(s.y_mm) || 0}
                  onCommit={(v) => setShelfY(si, v)}
                  min={0}/>
                <button type="button"
                        data-testid={`cab-bay-${bayIndex}-shelf-${si}-remove`}
                        onClick={() => removeShelf(si)}
                        aria-label={`Remove shelf ${si + 1}`}
                        title="Remove shelf"
                        className="w-7 h-7 inline-flex items-center justify-center border border-border hover:border-foreground">
                  <Minus className="w-3 h-3"/>
                </button>
              </div>
              {/* Per-shelf material override (Stage C). */}
              <div className="pl-16 mt-0.5 min-w-0"
                   data-testid={`cab-bay-${bayIndex}-shelf-${si}-material`}>
                <MaterialPicker
                  componentType={componentType}
                  slotKey="top"
                  projectId={projectId}
                  currentId={s.material_id || ""}
                  onPick={(p) => setShelfMaterial(si, p.id)}
                  onClear={() => setShelfMaterial(si, null)}
                  compact
                  placeholder="Inherit shelf default material"/>
              </div>
            </div>
          ))}
          <button type="button"
                  data-testid={`cab-bay-${bayIndex}-shelf-add`}
                  onClick={addShelf}
                  disabled={shelves.length >= 8}
                  className="w-full h-8 text-[11px] border border-border hover:border-foreground disabled:opacity-30">
            + Add shelf
          </button>
          {issues.length > 0 && (
            <div className="text-[10px] text-destructive space-y-0.5"
                 data-testid={`cab-bay-${bayIndex}-shelf-validation`}>
              {issues.map((iss) => (<div key={`${iss.i}-${iss.kind}`}>{iss.msg}</div>))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Draft-commit integer input ─────────────────────────────────────
// Reusable numeric input that follows the V3 Bay Count pattern:
// temporary edit state accepted while focused; commit only on blur
// or Enter. Escape reverts to the last committed value. Empty commit
// restores the previous canonical.
function DraftIntInput({ value, onCommit, testid, min = 0, className = "" }) {
  const [draft, setDraft] = useState(String(value ?? 0));
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) setDraft(String(value ?? 0));
  }, [value, focused]);
  const commit = () => {
    if (draft === "" || draft === null || draft === undefined) {
      setDraft(String(value ?? 0));
      return;
    }
    const parsed = parseInt(draft, 10);
    if (Number.isNaN(parsed) || parsed < min) {
      setDraft(String(value ?? 0));
      return;
    }
    onCommit(parsed);
  };
  return (
    <input
      data-testid={testid}
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      className={`luxe-input flex-1 min-w-0 ${className}`}
      value={draft}
      onFocus={(e) => { setFocused(true); try { e.currentTarget.select(); } catch (_) {} }}
      onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, ""))}
      onBlur={() => { setFocused(false); commit(); }}
      onKeyDown={(e) => {
        if (e.key === "Enter") { e.preventDefault(); commit(); e.currentTarget.blur(); }
        else if (e.key === "Escape") { setDraft(String(value ?? 0)); e.currentTarget.blur(); }
      }}/>
  );
}

/**
 * DraftNumInput — lightweight draft-commit primitive shared by all the
 * ad-hoc numeric inputs that don't use the full <NumField> layout
 * (they supply their own label + className). Same rules apply:
 * temporary edit state, commit on blur / Enter, select-on-focus, empty
 * commit restores previous canonical value, decimals when `decimals`
 * is truthy.
 */
export function DraftNumInput({ value, onCommit, testid, min, max,
                                decimals = false, className = "luxe-input w-full min-w-0",
                                placeholder = "", "aria-label": ariaLabel }) {
  const canonical = value ?? "";
  const [draft, setDraft] = useState(String(canonical));
  const [focused, setFocused] = useState(false);
  useEffect(() => { if (!focused) setDraft(String(canonical)); }, [canonical, focused]);
  const commit = () => {
    if (draft === "" || draft === "-" || draft === "." || draft === "-.") {
      setDraft(String(canonical));
      // For fields where empty is a valid clear (e.g. lighting custom
      // Kelvin) callers can pass value=null and interpret restoration
      // themselves; the standard rule here is: empty restores previous.
      return;
    }
    const n = decimals ? parseFloat(draft) : parseInt(draft, 10);
    if (Number.isNaN(n)) { setDraft(String(canonical)); return; }
    let out = n;
    if (typeof min === "number" && out < min) out = min;
    if (typeof max === "number" && out > max) out = max;
    onCommit(out);
    setDraft(String(out));
  };
  const filter = (raw) => {
    let out = String(raw).replace(/[^0-9.\-]/g, "");
    if (out.indexOf("-") > 0) out = out.replace(/-/g, "");
    if (!decimals) out = out.replace(/\./g, "");
    const parts = out.split(".");
    if (parts.length > 2) out = parts[0] + "." + parts.slice(1).join("");
    return out;
  };
  return (
    <input data-testid={testid}
           type="text"
           inputMode={decimals ? "decimal" : "numeric"}
           pattern={decimals ? "[0-9.\\-]*" : "[0-9\\-]*"}
           className={className}
           placeholder={placeholder}
           aria-label={ariaLabel}
           value={draft}
           onFocus={(e) => { setFocused(true); try { e.currentTarget.select(); } catch (_) {} }}
           onChange={(e) => setDraft(filter(e.target.value))}
           onBlur={() => { setFocused(false); commit(); }}
           onKeyDown={(e) => {
             if (e.key === "Enter") { e.preventDefault(); commit(); e.currentTarget.blur(); }
             else if (e.key === "Escape") { setDraft(String(canonical)); e.currentTarget.blur(); }
           }}/>
  );
}



// ── Door-opening segmented icon control ────────────────────────────
// Three-way visual selector for canonical door handing. Each icon is
// a miniature door rectangle drawn with the SAME triangular elevation
// notation used by the main 2D schematic: diagonals originate from
// the HINGE side corners and converge at the midpoint of the HANDLE
// side. Values map 1-to-1 onto canonical `bay.hinge`.
function HingeSegmented({ value, onChange, bayIndex = 0 }) {
  const btn = (v, label, svg) => {
    const active = value === v;
    return (
      <button type="button"
              data-testid={`cab-bay-${bayIndex}-hinge-${v}`}
              onClick={() => onChange(v)}
              aria-label={label}
              aria-pressed={active}
              title={label}
              className={`flex-1 min-h-[44px] inline-flex items-center justify-center border transition-colors ${active ? "border-foreground bg-foreground/10" : "border-border hover:border-foreground/60"}`}>
        {svg}
        <span className="sr-only">{label}</span>
      </button>
    );
  };
  // 28×20 miniature door — matches main 2D triangular convention.
  const leftIcon = (
    <svg width="28" height="20" viewBox="0 0 28 20" className="text-foreground/85">
      <rect x="1" y="1" width="26" height="18" fill="none" stroke="currentColor" strokeWidth="1"/>
      <line x1="1" y1="1"  x2="27" y2="10" stroke="currentColor" strokeWidth="1"/>
      <line x1="1" y1="19" x2="27" y2="10" stroke="currentColor" strokeWidth="1"/>
    </svg>
  );
  const rightIcon = (
    <svg width="28" height="20" viewBox="0 0 28 20" className="text-foreground/85">
      <rect x="1" y="1" width="26" height="18" fill="none" stroke="currentColor" strokeWidth="1"/>
      <line x1="27" y1="1"  x2="1" y2="10" stroke="currentColor" strokeWidth="1"/>
      <line x1="27" y1="19" x2="1" y2="10" stroke="currentColor" strokeWidth="1"/>
    </svg>
  );
  const pairIcon = (
    <svg width="36" height="20" viewBox="0 0 36 20" className="text-foreground/85">
      <rect x="1"  y="1" width="17" height="18" fill="none" stroke="currentColor" strokeWidth="1"/>
      <rect x="18" y="1" width="17" height="18" fill="none" stroke="currentColor" strokeWidth="1"/>
      <line x1="1"  y1="1"  x2="18" y2="10" stroke="currentColor" strokeWidth="1"/>
      <line x1="1"  y1="19" x2="18" y2="10" stroke="currentColor" strokeWidth="1"/>
      <line x1="35" y1="1"  x2="18" y2="10" stroke="currentColor" strokeWidth="1"/>
      <line x1="35" y1="19" x2="18" y2="10" stroke="currentColor" strokeWidth="1"/>
    </svg>
  );
  return (
    <div className="flex gap-1 w-full" data-testid={`cab-bay-${bayIndex}-hinge`}>
      {btn("left",  "Left Hinged",       leftIcon)}
      {btn("right", "Right Hinged",      rightIcon)}
      {btn("pair",  "Pair / Double Doors", pairIcon)}
    </div>
  );
}

// ── Fireplace Configuration Panel ─────────────────────────────────
// Canonical fireplace configuration: sided_type (1-sided_front,
// 2-sided_front_left, 2-sided_front_right, 2-sided_see_through, etc.)
// AND the actual visible_faces array so we always store which faces
// are visible — not just the count. Downstream 3D + construction docs
// consume visible_faces directly.
function FireplaceConfigPanel({ it, onConfig }) {
  const cfg = it.fireplace_config || { sided_type: "1_sided_front", visible_faces: ["front"] };
  const setSided = (v) => {
    const pre = FIREPLACE_SIDED_TYPES.find((s) => s.v === v);
    onConfig({ sided_type: v, visible_faces: pre ? [...pre.faces] : cfg.visible_faces });
  };
  const toggleFace = (face) => {
    const has = (cfg.visible_faces || []).includes(face);
    const nextFaces = has
      ? (cfg.visible_faces || []).filter((f) => f !== face)
      : [...(cfg.visible_faces || []), face];
    onConfig({ ...cfg, sided_type: "custom", visible_faces: nextFaces });
  };
  return (
    <div className="pt-3 border-t border-border" data-testid="fireplace-config">
      <div className="overline text-[10px] mb-2">Fireplace configuration</div>
      {/* Canonical live face diagram: top-down square showing which
          physical faces are visible. Room-facing side is at the
          bottom. Used by 3D + construction docs later. */}
      <div className="mb-3 p-2 border border-border bg-muted/30 flex justify-center">
        <FireplaceFacePreview fireplaceConfig={cfg} size={96}
                              testid="fp-face-preview"/>
      </div>
      <label className="overline text-[10px] block mb-1">Sided type</label>
      <select data-testid="fp-sided-type" className="luxe-input mb-2"
              value={cfg.sided_type || "1_sided_front"}
              onChange={(e) => setSided(e.target.value)}>
        {FIREPLACE_SIDED_TYPES.map((s) => (
          <option key={s.v} value={s.v}>{s.l}</option>
        ))}
        {cfg.sided_type === "custom" && <option value="custom">Custom</option>}
      </select>
      <div className="overline text-[10px] mb-1">Visible faces</div>
      <div className="grid grid-cols-2 gap-1 text-[11px]">
        {["front", "left", "right", "rear"].map((face) => {
          const active = (cfg.visible_faces || []).includes(face);
          return (
            <button key={face} type="button"
                    data-testid={`fp-face-${face}`}
                    aria-pressed={active}
                    onClick={() => toggleFace(face)}
                    className={`px-2 py-1.5 border transition-colors ${active ? "bg-foreground text-background border-foreground" : "border-border text-muted-foreground hover:text-foreground"}`}>
              {face[0].toUpperCase()}{face.slice(1)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SymbolExtrasPanel({ it, onMountHeight, onCircuit }) {
  return (
    <div className="pt-3 border-t border-border grid grid-cols-2 gap-2" data-testid="symbol-extras">
      <NumField testid="prop-mounting-height" label="Mount height (mm)"
                value={it.mounting_height_mm ?? it.y} onChange={onMountHeight}/>
      <div>
        <label className="overline text-[10px] block mb-1">Circuit</label>
        <input data-testid="prop-circuit" className="luxe-input"
               value={it.circuit || ""}
               onChange={(e) => onCircuit(e.target.value)}/>
      </div>
    </div>
  );
}

// ── Component Clearance / Keep-Out Zone panel ─────────────────────
// User-defined only — never pre-populated with manufacturer numbers.
// Default disabled. When enabled: uniform (single value → all 4 sides)
// or per_side (top/bottom/left/right). Enforcement is advisory | warn
// | block with default = warn. Values participate in scene_hash and
// freeze in Design Options via the backend canon.
const CLEARANCE_ENFORCEMENT = [
  { v: "advisory", l: "Advisory (info only)" },
  { v: "warn",     l: "Warn (visible caution)" },
  { v: "block",    l: "Block (prevent conflicts)" },
];
const CLEARANCE_SOURCES = [
  { v: "user_defined",             l: "User Defined" },
  { v: "luxe_default",             l: "Studio Default" },
  { v: "product_specification",    l: "Product Specification" },
  { v: "manufacturer_requirement", l: "Manufacturer Requirement" },
  { v: "project_requirement",      l: "Project Requirement" },
];
function ClearancePanel({ it, onConfig }) {
  const cfg = it.clearance_config || { enabled: false };
  const setField = (patch) => onConfig({ ...cfg, ...patch });
  const setUniform = (v) => onConfig({
    ...cfg, mode: "uniform", top_mm: v, bottom_mm: v, left_mm: v, right_mm: v,
  });
  // If the current source is a freeform legacy string that doesn't
  // match one of the canonical enums, we still allow the select to
  // reflect it via a temporary "custom" option so nothing is lost.
  const sourceValue = cfg.source || "user_defined";
  const sourceKnown = CLEARANCE_SOURCES.some((o) => o.v === sourceValue);
  return (
    <div className="pt-3 border-t border-border" data-testid="clearance-config">
      {/* Full-width heading row — heading stays on ONE line, Enabled
          toggle sits on its own row below when width is tight. Prevents
          "Clearance /" wrapping into "Keep-Out" on narrow panels. */}
      <div className="mb-2 min-w-0">
        <div className="overline text-[10px] whitespace-nowrap"
             data-testid="clearance-heading">
          Clearance / Keep-Out
        </div>
      </div>
      <label className="inline-flex items-center gap-2 text-[11px] mb-2">
        <input type="checkbox"
               data-testid="clearance-enabled"
               checked={!!cfg.enabled}
               onChange={(e) => {
                 const enabled = e.target.checked;
                 if (enabled && !cfg.mode) {
                   onConfig({
                     enabled: true, mode: "uniform",
                     top_mm: 0, bottom_mm: 0, left_mm: 0, right_mm: 0,
                     source: "user_defined", enforcement: "warn",
                   });
                 } else {
                   setField({ enabled });
                 }
               }}/>
        <span>Enabled</span>
      </label>
      {!cfg.enabled ? (
        <div className="text-[10px] text-muted-foreground italic">
          Disabled. No clearance requirement is enforced or documented.
        </div>
      ) : (
        <>
          {/* Mode — full-width row so "Uniform (all sides)" / "Per side"
              stay fully readable. */}
          <div className="mb-2">
            <label className="overline text-[10px] block mb-1">Mode</label>
            <select data-testid="clearance-mode"
                    className="luxe-input w-full min-w-0"
                    value={cfg.mode || "uniform"}
                    onChange={(e) => setField({ mode: e.target.value })}>
              <option value="uniform">All Sides</option>
              <option value="per_side">Per Side</option>
            </select>
          </div>
          {/* Source — full-width canonical enum. Values kept in
              CLEARANCE_SOURCES so the trigger label is always the
              approved user-facing wording. */}
          <div className="mb-2">
            <label className="overline text-[10px] block mb-1">Source</label>
            <select data-testid="clearance-source"
                    className="luxe-input w-full min-w-0"
                    value={sourceKnown ? sourceValue : "__custom__"}
                    onChange={(e) => setField({ source: e.target.value === "__custom__" ? sourceValue : e.target.value })}>
              {CLEARANCE_SOURCES.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
              {!sourceKnown && (
                <option value="__custom__">Custom: {sourceValue}</option>
              )}
            </select>
          </div>
          {/* Enforcement — full-width so "Warn (visible caution)" is
              fully visible. */}
          <div className="mb-2">
            <label className="overline text-[10px] block mb-1">Enforcement</label>
            <select data-testid="clearance-enforcement"
                    className="luxe-input w-full min-w-0"
                    value={cfg.enforcement || "warn"}
                    onChange={(e) => setField({ enforcement: e.target.value })}>
              {CLEARANCE_ENFORCEMENT.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </div>
          {cfg.mode === "uniform" ? (
            <NumField testid="clearance-uniform"
                       label="All sides (mm)"
                       value={Math.round(cfg.top_mm || 0)}
                       onChange={setUniform}/>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <NumField testid="clearance-top-mm"    label="Top (mm)"    value={Math.round(cfg.top_mm    || 0)} onChange={(v) => setField({ top_mm: v })}/>
              <NumField testid="clearance-bottom-mm" label="Bottom (mm)" value={Math.round(cfg.bottom_mm || 0)} onChange={(v) => setField({ bottom_mm: v })}/>
              <NumField testid="clearance-left-mm"   label="Left (mm)"   value={Math.round(cfg.left_mm   || 0)} onChange={(v) => setField({ left_mm: v })}/>
              <NumField testid="clearance-right-mm"  label="Right (mm)"  value={Math.round(cfg.right_mm  || 0)} onChange={(v) => setField({ right_mm: v })}/>
            </div>
          )}
          {/* Optional user note kept as a full-width free-text field so
              it does not compete with the canonical Source select. */}
          <div className="mt-2">
            <label className="overline text-[10px] block mb-1">Notes</label>
            <input data-testid="clearance-notes"
                   className="luxe-input w-full min-w-0"
                   placeholder="e.g. per manufacturer datasheet §4.2"
                   value={cfg.notes || ""}
                   onChange={(e) => setField({ notes: e.target.value })}/>
          </div>
          <div className="text-[10px] text-muted-foreground mt-1">
            Values are user-defined. No invented manufacturer numbers — use verified product data where available.
          </div>
        </>
      )}
    </div>
  );
}


function AlignLayerPanel(props) {
  const {
    onCenterH, onCenterV, onCenterBoth,
    onAlignLeft, onAlignRight, onAlignTop, onAlignBottom,
    onBringForward, onSendBackward, onBringToFront, onSendToBack,
    visualLayer, selected, updateSelected,
  } = props;
  // Compact icon-only alignment toolbar — 3×2 grid (H: L/C/R, V: T/M/B).
  // Each button is 32×32 with an aria-label + title tooltip. No visible
  // text inside the button (readability/containment fix).
  const abtn = "w-8 h-8 inline-flex items-center justify-center border border-border hover:border-foreground transition-colors";
  return (
    <div className="pt-3 border-t border-border" data-testid="align-panel">
      <div className="overline text-[10px] mb-2">Alignment</div>
      <div className="grid grid-cols-3 gap-1 mb-2">
        {/* Row 1 — Horizontal: Left / Centre / Right */}
        <button data-testid="btn-align-left" onClick={onAlignLeft}
                aria-label="Align left" title="Align left" className={abtn}>
          <ArrowLeftToLine className="w-4 h-4" strokeWidth={1.5}/>
        </button>
        <button data-testid="btn-center-h" onClick={onCenterH}
                aria-label="Align centre" title="Align centre" className={abtn}>
          <AlignHorizontalJustifyCenter className="w-4 h-4" strokeWidth={1.5}/>
        </button>
        <button data-testid="btn-align-right" onClick={onAlignRight}
                aria-label="Align right" title="Align right" className={abtn}>
          <ArrowRightToLine className="w-4 h-4" strokeWidth={1.5}/>
        </button>
        {/* Row 2 — Vertical: Top / Middle / Bottom */}
        <button data-testid="btn-align-top" onClick={onAlignTop}
                aria-label="Align top" title="Align top" className={abtn}>
          <ArrowUpToLine className="w-4 h-4" strokeWidth={1.5}/>
        </button>
        <button data-testid="btn-center-v" onClick={onCenterV}
                aria-label="Align middle" title="Align middle" className={abtn}>
          <AlignCenter className="w-4 h-4" strokeWidth={1.5}/>
        </button>
        <button data-testid="btn-align-bottom" onClick={onAlignBottom}
                aria-label="Align bottom" title="Align bottom" className={abtn}>
          <ArrowDownToLine className="w-4 h-4" strokeWidth={1.5}/>
        </button>
      </div>
      <button data-testid="btn-center-both" onClick={onCenterBoth}
              title="Centre on wall (both axes)"
              className="w-full btn-outline text-[10px] py-1.5 min-h-[32px]">
        Centre on wall
      </button>
      <div className="overline text-[10px] mt-3 mb-2">Visual Layer <span className="text-muted-foreground/70 normal-case tracking-normal">(2D draw order — layer {visualLayer ?? "—"})</span></div>
      {/* Compact 4-icon toolbar. Order — left→right — walks the stack
          from back to front: [Send to Back] [Send Backward] [Bring
          Forward] [Bring to Front]. Icon-only with aria-label + title
          tooltips so meaning stays clear on desktop, touch and screen
          readers. Buttons keep their slots when disabled (layout
          stability) rather than disappearing. */}
      <div className="grid grid-cols-4 gap-1">
        <button data-testid="btn-send-back" onClick={onSendToBack}
                aria-label="Send to back" title="Send to back"
                className="w-full min-h-[36px] inline-flex items-center justify-center border border-border hover:border-foreground transition-colors">
          <ChevronsDown className="w-4 h-4" strokeWidth={1.5}/>
          <span className="sr-only">Send to back</span>
        </button>
        <button data-testid="btn-send-backward" onClick={onSendBackward}
                aria-label="Send backward" title="Send backward"
                className="w-full min-h-[36px] inline-flex items-center justify-center border border-border hover:border-foreground transition-colors">
          <ChevronDown className="w-4 h-4" strokeWidth={1.5}/>
          <span className="sr-only">Send backward</span>
        </button>
        <button data-testid="btn-bring-forward" onClick={onBringForward}
                aria-label="Bring forward" title="Bring forward"
                className="w-full min-h-[36px] inline-flex items-center justify-center border border-border hover:border-foreground transition-colors">
          <ChevronUp className="w-4 h-4" strokeWidth={1.5}/>
          <span className="sr-only">Bring forward</span>
        </button>
        <button data-testid="btn-bring-front" onClick={onBringToFront}
                aria-label="Bring to front" title="Bring to front"
                className="w-full min-h-[36px] inline-flex items-center justify-center border border-border hover:border-foreground transition-colors">
          <ChevronsUp className="w-4 h-4" strokeWidth={1.5}/>
          <span className="sr-only">Bring to front</span>
        </button>
      </div>
      <div className="text-[10px] text-muted-foreground mt-1">
        Visual order = 2D stacking only. Construction sequence lives in Assembly Order below.
      </div>
      <div className="overline text-[10px] mt-3 mb-1">Assembly Order (construction stack)</div>
      <DraftNumInput testid="prop-assembly-order"
                     value={selected.assembly_order ?? ""}
                     placeholder="1 = framing · 2 = substrate · 3 = finish · 4 = mounted"
                     onCommit={(v) => updateSelected({ assembly_order: v })}/>
    </div>
  );
}

function MaterialSection({ it, projectId, onAssignMaterial, onUpdate }) {
  const slots = slotsFor(it.type);
  const slotKeys = Object.keys(slots);
  if (slotKeys.length) {
    return (
      <div className="mt-3 pt-3 border-t border-border">
        <div className="overline text-[10px] mb-2">Materials</div>
        <div className="space-y-3" data-testid="multi-slot-panel">
          {slotKeys.map((sk) => (
            <SlotRow key={sk} sk={sk} slot={slots[sk]} it={it} projectId={projectId} onUpdate={onUpdate}/>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className="mt-3 pt-3 border-t border-border">
      <div className="overline text-[10px] mb-1">Material</div>
      <MaterialPicker
        componentType={it.type}
        projectId={projectId}
        currentId={it.material_id || it.product_id || ""}
        onPick={(p) => onAssignMaterial(p)}
        onClear={() => onUpdate({ material_id: null, product_id: null })}
      />
    </div>
  );
}
function SlotRow({ sk, slot, it, projectId, onUpdate }) {
  const slotState = (it.material_slots || {})[sk] || {};
  const cur = slotState.material_id || slotState.product_id || "";
  const [category, setCategory] = useState(null);
  // Fetch the current material's category so MaterialMappingPanel can
  // decide whether to show timber grain vs stone slab controls. Cached
  // per material_id — no round-trip on every render.
  useEffect(() => {
    let cancelled = false;
    if (!cur) { setCategory(null); return () => { cancelled = true; }; }
    api.get(`/products/${cur}`).then((r) => {
      if (!cancelled) setCategory(r.data?.category || null);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [cur]);
  return (
    <div data-testid={`slot-${sk}`}
         className="p-2 border border-border rounded-sm bg-card/40 min-w-0">
      <div className="text-[11px] font-medium mb-2 whitespace-nowrap">
        {slot.label}{slot.required && <span className="text-red-500"> *</span>}
      </div>
      <MaterialPicker
        componentType={it.type}
        slotKey={sk}
        projectId={projectId}
        currentId={cur}
        onPick={(p) => {
          const nextSlots = { ...(it.material_slots || {}) };
          const prevMapping = (nextSlots[sk] || {}).mapping;
          nextSlots[sk] = {
            material_id: p.id, product_id: null,
            ...(prevMapping ? { mapping: prevMapping } : {}),
          };
          onUpdate({ material_slots: nextSlots });
        }}
        onClear={() => {
          const nextSlots = { ...(it.material_slots || {}) };
          delete nextSlots[sk];
          onUpdate({ material_slots: nextSlots });
        }}
        compact
      />
      {cur && (
        <MaterialMappingPanel
          slotKey={sk}
          mapping={slotState.mapping || null}
          materialCategory={category}
          onChange={(nextMapping) => {
            const nextSlots = { ...(it.material_slots || {}) };
            const cur2 = { ...(nextSlots[sk] || {}) };
            if (nextMapping === null) {
              delete cur2.mapping;
            } else {
              cur2.mapping = nextMapping;
            }
            nextSlots[sk] = cur2;
            onUpdate({ material_slots: nextSlots });
          }}/>
      )}
    </div>
  );
}

function QuantitySyncSection({ it, onDoSync, onSyncItem, onQtyMode, onWastage }) {
  const def = getComponent(it.type) || {};
  const allowed = def.allowed_qms || ["each"];
  const hasSlot = Object.values(it.material_slots || {}).some((s) => s && (s.material_id || s.product_id));
  const canSync = !!(it.product_id || it.material_id || hasSlot);
  return (
    <div className="mt-3 pt-3 border-t border-border space-y-2">
      {/* Full-width stacked rows — "Qty Mode" and "Wastage" must never
          be forced into narrow columns that clip their values. Per the
          app-wide Control Readability Rule. */}
      <div className="min-w-0">
        <label className="overline text-[10px] block mb-1 whitespace-nowrap">Qty Mode</label>
        <select data-testid="prop-qty-mode" className="luxe-input w-full min-w-0"
                value={it.quantity_mode || def.default_qm || "manual"}
                onChange={(e) => onQtyMode(e.target.value)}>
          {allowed.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
          {allowed.length > 1 && <option value="manual">manual</option>}
        </select>
      </div>
      <div className="min-w-0">
        <label className="overline text-[10px] block mb-1 whitespace-nowrap">Wastage</label>
        <div className="relative">
          <DraftNumInput testid="prop-wastage"
                         value={it.wastage_pct || 0}
                         min={0} max={100}
                         decimals={true}
                         className="luxe-input w-full pr-8 min-w-0"
                         onCommit={(v) => onWastage(v)}/>
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-muted-foreground pointer-events-none">%</span>
        </div>
      </div>
      {onSyncItem && (
        <button data-testid="btn-sync-to-estimate" onClick={onDoSync}
                disabled={!canSync}
                className="btn-accent w-full justify-center mt-1 text-xs disabled:opacity-40">
          Sync to Estimate
        </button>
      )}
    </div>
  );
}


// ── Schematic Items panel + rows ─────────────────────────────────────
export function SchematicItemsBody(props) {
  const { items, selectedId, view, onViewChange, search, onSearchChange,
          onSelect, onRequestDelete, visualLayerOf } = props;
  const q = search.trim().toLowerCase();

  const drawSorted = useMemo(() => {
    const filtered = q
      ? items.filter((it) => {
          const def = getComponent(it.type) || {};
          const hay = [displayName(it), def.label, it.type, def.category,
                       it.substrate, it.circuit, it.legend_code, subtitleFor(it)]
            .filter(Boolean).join(" ").toLowerCase();
          return hay.includes(q);
        })
      : items;
    return [...filtered].sort((a, b) => (b.draw_order || 0) - (a.draw_order || 0));
  }, [items, q]);

  return (
    <div className="text-xs space-y-2" data-testid="schematic-items-panel">
      <div>
        <input data-testid="items-search"
               aria-label="Filter schematic items"
               className="luxe-input min-h-[36px] text-[12px]"
               placeholder="Filter items…"
               value={search}
               onChange={(e) => onSearchChange(e.target.value)}/>
      </div>
      <div className="flex text-[10px] uppercase tracking-widest gap-1" data-testid="items-view-tabs">
        <ViewTab k="draw" label="Front→Back" cur={view} onSelect={onViewChange}/>
        <ViewTab k="type" label="By Type"    cur={view} onSelect={onViewChange}/>
        <ViewTab k="host" label="By Host"    cur={view} onSelect={onViewChange}/>
      </div>

      {items.length === 0 && (
        <div className="text-[11px] text-muted-foreground pt-4" data-testid="items-empty">
          No schematic items yet. Add one from the palette on the left.
        </div>
      )}
      {items.length > 0 && drawSorted.length === 0 && (
        <div className="text-[11px] text-muted-foreground pt-4" data-testid="items-search-empty">
          No items match “{search}”.
        </div>
      )}

      {view === "draw" && (
        <ul className="space-y-1" data-testid="items-list-draw">
          {drawSorted.map((it) => (
            <ItemRow key={it.id} item={it}
                     hostItem={items.find((h) => h.id === it.host_id)}
                     selected={it.id === selectedId}
                     visualLayer={visualLayerOf(it.id)}
                     onSelect={onSelect} onDelete={onRequestDelete}/>
          ))}
        </ul>
      )}
      {view === "type" && (
        <TypeGroups items={items} drawSorted={drawSorted} selectedId={selectedId}
                    visualLayerOf={visualLayerOf}
                    onSelect={onSelect} onRequestDelete={onRequestDelete}/>
      )}
      {view === "host" && (
        <HostTree items={items} drawSorted={drawSorted} selectedId={selectedId}
                  visualLayerOf={visualLayerOf}
                  onSelect={onSelect} onRequestDelete={onRequestDelete}/>
      )}
    </div>
  );
}

function ViewTab({ k, label, cur, onSelect }) {
  const active = cur === k;
  return (
    <button data-testid={`items-view-${k}`}
            aria-selected={active}
            onClick={() => onSelect(k)}
            className={`flex-1 min-h-[32px] px-2 border ${active ? "border-foreground bg-foreground text-background" : "border-border text-muted-foreground hover:text-foreground"}`}>
      {label}
    </button>
  );
}

function TypeGroups({ items, drawSorted, selectedId, visualLayerOf, onSelect, onRequestDelete }) {
  const buckets = {};
  for (const it of drawSorted) {
    const cat = (getComponent(it.type) || {}).category || "Other";
    (buckets[cat] = buckets[cat] || []).push(it);
  }
  const cats = Object.keys(buckets).sort();
  return (
    <div className="space-y-3" data-testid="items-list-type">
      {cats.map((cat) => (
        <div key={cat} data-testid={`items-type-${cat}`}>
          <div className="overline text-[10px] text-muted-foreground mb-1">{cat}</div>
          <ul className="space-y-1">
            {buckets[cat].map((it) => (
              <ItemRow key={it.id} item={it}
                       hostItem={items.find((h) => h.id === it.host_id)}
                       selected={it.id === selectedId}
                       visualLayer={visualLayerOf(it.id)}
                       onSelect={onSelect} onDelete={onRequestDelete}/>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function HostTree({ items, drawSorted, selectedId, visualLayerOf, onSelect, onRequestDelete }) {
  const setFilt = new Set(drawSorted.map((i) => i.id));
  const byHost = {};
  const rootIds = [];
  for (const it of items) {
    if (!setFilt.has(it.id)) continue;
    const h = it.host_id;
    if (h && items.find((x) => x.id === h)) {
      (byHost[h] = byHost[h] || []).push(it);
    } else {
      rootIds.push(it.id);
    }
  }
  return (
    <HostTreeList items={items} byHost={byHost} rootIds={rootIds}
                  selectedId={selectedId} visualLayerOf={visualLayerOf}
                  onSelect={onSelect} onDelete={onRequestDelete}/>
  );
}

function ItemRow({ item, hostItem, selected, visualLayer, onSelect, onDelete }) {
  const sub = subtitleFor(item);
  return (
    <li data-row-id={item.id} data-testid={`items-row-${item.id}`}
        aria-selected={selected}
        className={`group flex items-center gap-2 border border-transparent px-2 py-2 min-h-[44px] cursor-pointer transition-colors ${selected ? "bg-muted border-foreground" : "hover:border-border hover:bg-muted/50"}`}
        onClick={() => onSelect(item.id)}>
      <div className="min-w-0 flex-1">
        <div className="text-[12px] font-medium truncate">
          {displayName(item)}
          {sub ? <span className="text-muted-foreground font-normal"> — {sub}</span> : null}
        </div>
        <div className="text-[10px] text-muted-foreground truncate flex items-center gap-2">
          <span>Host: {hostItem ? displayName(hostItem) : "Wall"}</span>
          <span>·</span>
          <span data-testid={`items-row-visual-${item.id}`}>Visual: {visualLayer ?? "—"}</span>
          <span>·</span>
          <span data-testid={`items-row-assembly-${item.id}`}>Assembly: {item.assembly_order ?? "—"}</span>
        </div>
      </div>
      {item.locked && (
        <span data-testid={`items-row-lock-${item.id}`} className="text-foreground/70" title="Locked">
          <Lock className="w-3 h-3" strokeWidth={1.5}/>
        </span>
      )}
      <button type="button" data-testid={`items-row-delete-${item.id}`}
              disabled={item.locked}
              title={item.locked ? "Unlock to delete" : "Delete"}
              onClick={(e) => { e.stopPropagation(); onDelete(item); }}
              className="text-muted-foreground hover:text-[color:hsl(var(--destructive))] disabled:opacity-30 min-w-[32px] min-h-[32px] flex items-center justify-center">
        <Trash2 className="w-3 h-3" strokeWidth={1.5}/>
      </button>
    </li>
  );
}


// ── Select Layer picker ─────────────────────────────────────────────
export function SelectLayerPicker({ picker, selectedId, onPick, onClose, visualLayerOf }) {
  const style = {
    left: Math.min(picker.x + 8, 400),
    top:  Math.max(4, picker.y - 8),
  };
  return (
    <div data-testid="select-layer-picker"
         onClick={(e) => e.stopPropagation()}
         className="absolute z-20 bg-card border border-foreground shadow-xl min-w-[220px] max-h-[300px] overflow-auto"
         style={style}>
      <div className="px-3 py-2 border-b border-border flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest inline-flex items-center gap-1">
          <Layers className="w-3 h-3" strokeWidth={1.5}/>
          {picker.hits.length} items here
        </div>
        <button type="button" data-testid="select-layer-close" onClick={onClose}
                className="text-muted-foreground hover:text-foreground text-[11px]">×</button>
      </div>
      <ul>
        {picker.hits.map((it, i) => (
          <LayerPickRow key={it.id} it={it} i={i} total={picker.hits.length}
                        selected={it.id === selectedId} onPick={onPick}
                        visualLayer={visualLayerOf(it.id)}/>
        ))}
      </ul>
    </div>
  );
}
function LayerPickRow({ it, i, total, selected, onPick, visualLayer }) {
  return (
    <li>
      <button type="button" data-testid={`select-layer-pick-${it.id}`}
              onClick={() => onPick(it.id)}
              className={`w-full text-left px-3 py-2 min-h-[44px] flex items-center justify-between gap-2 hover:bg-muted transition-colors ${selected ? "bg-muted" : ""}`}>
        <div className="min-w-0 flex-1">
          <div className="text-[12px] truncate">{displayName(it)}</div>
          <div className="text-[10px] text-muted-foreground truncate">
            Visual: {visualLayer ?? "—"}
            {it.locked ? " · locked" : ""}
          </div>
        </div>
        <span className="text-[9px] text-muted-foreground">
          {i === 0 ? "Front" : (i === total - 1 ? "Back" : "")}
        </span>
      </button>
    </li>
  );
}


// ── Delete confirmation modal ────────────────────────────────────────
export function DeleteConfirm({ pending, onCancel, onDetach, onCascade }) {
  const { item, children } = pending;
  const hasChildren = children.length > 0;
  return (
    <div data-testid="delete-confirm"
         className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()}
           className="bg-card border border-border p-6 max-w-md w-full space-y-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" strokeWidth={1.5}/>
          <div className="flex-1">
            <div className="text-sm font-medium">Delete {displayName(item)}?</div>
            {hasChildren ? (
              <div className="text-[11px] text-muted-foreground mt-1" data-testid="delete-children-warning">
                This item hosts {children.length} component{children.length === 1 ? "" : "s"}:
                <ul className="mt-1 ml-3 list-disc">
                  {children.map((c) => (
                    <li key={c.id}>{displayName(c)}</li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="text-[11px] text-muted-foreground mt-1">
                This action cannot be undone from history after saving.
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2">
          {hasChildren && (
            <>
              <button type="button" data-testid="btn-delete-detach" onClick={onDetach}
                      className="btn-outline text-xs justify-center min-h-[44px]">
                Detach children and delete host
              </button>
              <button type="button" data-testid="btn-delete-cascade" onClick={onCascade}
                      className="text-xs justify-center min-h-[44px] px-3 inline-flex items-center border border-[color:hsl(var(--destructive))] text-[color:hsl(var(--destructive))] hover:bg-[color:hsl(var(--destructive))] hover:text-white transition-colors">
                Delete host and hosted children ({children.length})
              </button>
            </>
          )}
          {!hasChildren && (
            <button type="button" data-testid="btn-delete-confirm" onClick={onDetach}
                    className="text-xs justify-center min-h-[44px] px-3 inline-flex items-center border border-[color:hsl(var(--destructive))] text-[color:hsl(var(--destructive))] hover:bg-[color:hsl(var(--destructive))] hover:text-white transition-colors">
              Delete
            </button>
          )}
          <button type="button" data-testid="btn-delete-cancel" onClick={onCancel}
                  className="btn-outline text-xs justify-center min-h-[44px]">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
